export const DOCUMENT_DELETE_BLOCKED_MESSAGE =
  'Cannot delete this document because it is approved, pending approval, or has progressed to the next stage.';

const LOCKED_APPROVAL_STATUSES = ['approved', 'pending', 'pending approval'];

function norm(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

function inSet(value: string | null | undefined, allowed: string[]): boolean {
  const v = norm(value);
  return allowed.some((s) => s.toLowerCase() === v);
}

export type DeletableCheck = { ok: boolean; reason?: string };

function fromApiFields(row: {
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}): DeletableCheck | null {
  if (row.can_delete === true) return { ok: true };
  if (row.can_delete === false) {
    return { ok: false, reason: row.delete_blocked_reason || DOCUMENT_DELETE_BLOCKED_MESSAGE };
  }
  return null;
}

function blocked(reason?: string): DeletableCheck {
  return { ok: false, reason: reason || DOCUMENT_DELETE_BLOCKED_MESSAGE };
}

function allowed(): DeletableCheck {
  return { ok: true };
}

export function canEditQuotation(row: {
  status?: string | null;
  approval_status?: string | null;
  is_converted_to_sales_order?: boolean;
  sales_order_id?: string | null;
}): DeletableCheck {
  if (row.is_converted_to_sales_order || row.sales_order_id) {
    return {
      ok: false,
      reason: 'This quotation has been converted to a customer order and cannot be edited.',
    };
  }

  const status = norm(row.status);
  const approval = norm(row.approval_status);

  if (status === 'sent') {
    return { ok: false, reason: 'Cannot update sent quotation. Create a revision instead.' };
  }
  if (status === 'accepted') {
    return { ok: false, reason: 'Cannot update accepted quotation' };
  }
  if (status === 'revised') {
    return { ok: false, reason: 'Cannot update a superseded (Revised) quotation row.' };
  }
  if (approval === 'approved') {
    return {
      ok: false,
      reason: 'Approved quotations are immutable. Create a revision to change commercial content.',
    };
  }

  return { ok: true };
}

export function canDeleteQuotation(row: {
  status?: string;
  approval_status?: string;
  is_converted_to_sales_order?: boolean;
  is_converted_to_project?: boolean;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}): DeletableCheck {
  const api = fromApiFields(row);
  if (api) return api;
  if (LOCKED_APPROVAL_STATUSES.includes(norm(row.approval_status))) return blocked();
  if (!inSet(row.status, ['Draft', 'Revised'])) return blocked();
  if (row.is_converted_to_sales_order || row.is_converted_to_project) return blocked();
  return allowed();
}

export function canDeleteEstimation(row: {
  status?: string;
  approval_status?: string;
  is_converted_to_quotation?: boolean;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}): DeletableCheck {
  const api = fromApiFields(row);
  if (api) return api;
  if (LOCKED_APPROVAL_STATUSES.includes(norm(row.approval_status))) return blocked();
  if (!inSet(row.status, ['Draft'])) return blocked();
  if (row.is_converted_to_quotation) return blocked();
  return allowed();
}

export function canDeleteOpportunity(row: {
  stage?: string;
  is_converted_to_project?: boolean;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}): DeletableCheck {
  const api = fromApiFields(row);
  if (api) return api;
  if (row.is_converted_to_project) return blocked();
  if (!inSet(row.stage, ['Prospecting', 'Qualification'])) return blocked();
  return allowed();
}

export function canDeleteTradingRentalEstimation(row: {
  status?: string;
  approval_status?: string;
  related_quotation_count?: number;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}): DeletableCheck {
  const api = fromApiFields(row);
  if (api) return api;
  if (LOCKED_APPROVAL_STATUSES.includes(norm(row.approval_status))) return blocked();
  const status = norm(row.status);
  if (status === 'converted to quotation' || status === 'partially converted') return blocked();
  if (Number(row.related_quotation_count || 0) > 0) return blocked();
  if (!inSet(row.status, ['Draft', 'Cancelled'])) return blocked();
  return allowed();
}

export function canDeleteJournalVoucher(row: {
  status?: string;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}): DeletableCheck {
  const api = fromApiFields(row);
  if (api) return api;
  if (!inSet(row.status, ['Draft'])) return blocked();
  return allowed();
}

export function canDeleteQualityPlan(row: {
  status?: string;
}): DeletableCheck {
  if (!inSet(row.status, ['Draft'])) return blocked();
  return allowed();
}

export function canDeleteInspection(row: {
  status?: string;
}): DeletableCheck {
  if (!inSet(row.status, ['Scheduled', 'In Progress'])) return blocked();
  return allowed();
}
